/**
 * Reusable soft colour variants.
 *
 * Each key is a colour name; each value is the Tailwind class string that sets
 * background (-200), border (-300) and text (-600) from the design-system
 * palette. Bind it directly with `:class`, e.g. `:class="COLOR_VARIANTS.gray"`.
 * Reuse on any element — buttons, divs, badges, the cluster / period bars, etc.
 *
 * The full class strings are written out literally so Tailwind's content
 * scanner keeps them (do not build these names dynamically).
 */
export const COLOR_VARIANTS = {
  purple: "tw-bg-purple-200 tw-border tw-border-purple-300 tw-text-purple-600",
  blue: "tw-bg-blue-200 tw-border tw-border-blue-300 tw-text-blue-600",
  aqua: "tw-bg-aqua-200 tw-border tw-border-aqua-300 tw-text-aqua-600",
  yellow: "tw-bg-yellow-200 tw-border tw-border-yellow-300 tw-text-yellow-600",
  red: "tw-bg-red-200 tw-border tw-border-red-300 tw-text-red-600",
  green: "tw-bg-green-200 tw-border tw-border-green-300 tw-text-green-600",
  gray: "tw-bg-gray-50 tw-border tw-border-gray-100 tw-text-gray-800",
} as const;

export type ColorVariant = keyof typeof COLOR_VARIANTS;
export type ColorVariantClass = (typeof COLOR_VARIANTS)[ColorVariant];

/** Flat list of the colour class strings (e.g. to iterate in a colour picker). */
export const COLOR_VARIANT_CLASSES = Object.values(COLOR_VARIANTS);

/**
 * Colours a cluster bar can use, in the order the picker shows them. A subset
 * of COLOR_VARIANTS: gray is reserved for the empty/ghost bar, so it isn't
 * selectable for a cluster.
 */
export const CLUSTER_COLORS: ColorVariant[] = [
  "purple",
  "blue",
  "aqua",
  "yellow",
  "red",
  "green",
];
